import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PlayCircle, FileText, CheckCircle, Circle, ArrowLeft, ListChecks,
  CircleNotch, Star, Lock, Certificate, GraduationCap, Trophy, Paperclip, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enr, setEnr] = useState(null);
  const [active, setActive] = useState(null); // lesson object
  const [mode, setMode] = useState("lesson"); // lesson | quiz | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyCfg, setSurveyCfg] = useState({ questions: [] });
  const [sa, setSa] = useState({});
  const videoRef = useRef(null);
  const lastSent = useRef(0);

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get(`/courses/${id}`),
      api.get(`/enrollments/${id}`),
    ]);
    setCourse(c.data); setEnr(e.data);
    if (!active) {
      const first = c.data.modules?.[0]?.lessons?.[0];
      setActive(first);
    }
  };
  useEffect(() => { load().catch(() => navigate("/dashboard/cursos")); }, [id]);
  useEffect(() => {
    api.get("/survey/config").then((r) => {
      setSurveyCfg(r.data);
      const d = {};
      (r.data.questions || []).forEach((q) => { if (q.type === "rating") d[q.id] = 5; });
      setSa(d);
    }).catch(() => {});
  }, []);

  const allLessons = (course?.modules || []).flatMap((m) => m.lessons.map((l) => ({ ...l, moduleTitle: m.title })));
  const lp = enr?.lesson_progress || {};
  const isDone = (lid) => lp[lid]?.completed;

  const sendProgress = async (lessonId, pct) => {
    try {
      const { data } = await api.post("/progress", { course_id: id, lesson_id: lessonId, watched_pct: pct });
      setEnr((prev) => ({
        ...prev,
        progress_pct: data.progress_pct,
        lesson_progress: { ...prev.lesson_progress, [lessonId]: { watched_pct: pct, completed: data.lesson_completed } },
      }));
      return data;
    } catch { }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct - lastSent.current >= 10 || pct >= 95) {
      lastSent.current = pct;
      sendProgress(active.id, Math.round(pct));
    }
  };

  const markTextRead = async () => {
    await sendProgress(active.id, 100);
    toast.success("Lección completada");
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", { course_id: id, answers });
      setResult(data); setMode("result");
      await load();
      if (data.passed) { setSurveyOpen(true); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const submitSurvey = async () => {
    const ratingQ = surveyCfg.questions.find((q) => q.type === "rating");
    const commentQ = surveyCfg.questions.find((q) => q.type === "text");
    const payload = {
      course_id: id, answers: sa,
      rating: ratingQ ? Number(sa[ratingQ.id] || 0) : null,
      comment: commentQ ? (sa[commentQ.id] || "") : null,
    };
    try { await api.post("/survey", payload); toast.success("¡Gracias por tu opinión!"); } catch {}
    setSurveyOpen(false);
  };

  if (!course || !enr) return <div className="min-h-[60vh] flex items-center justify-center"><CircleNotch size={32} className="animate-spin text-primary" /></div>;

  const allComplete = allLessons.length > 0 && allLessons.every((l) => isDone(l.id));

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-[#1E293B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard/cursos" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft size={18} /> Mis Cursos
          </Link>
          <span className="text-sm font-medium truncate max-w-[50%]">{course.title}</span>
          <span className="text-sm font-mono">{enr.progress_pct}%</span>
        </div>
        <Progress value={enr.progress_pct} className="h-1 rounded-none" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-4 gap-6">
        {/* Content */}
        <div className="lg:col-span-3">
          {mode === "lesson" && active && (
            <div className="animate-fade-up">
              {active.type === "video" ? (
                <div className="bg-black rounded-lg overflow-hidden aspect-video">
                  <video ref={videoRef} key={active.id} controls onTimeUpdate={onTimeUpdate}
                    className="w-full h-full" data-testid="lesson-video" src={active.video_url} />
                </div>
              ) : (
                <div className="bg-white border border-border rounded-lg p-8" data-testid="lesson-text">
                  <div className="flex items-center gap-2 text-primary mb-4"><FileText size={20} /> <span className="text-xs uppercase tracking-overline font-mono">Lectura</span></div>
                  <p className="leading-relaxed text-foreground whitespace-pre-line">{active.content}</p>
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <div>
                  <p className="text-xs uppercase tracking-overline text-muted-foreground font-mono">{active.moduleTitle}</p>
                  <h2 className="font-display text-xl font-800 tracking-tight">{active.title}</h2>
                </div>
                {active.type === "text" ? (
                  <Button onClick={markTextRead} disabled={isDone(active.id)} data-testid="mark-read-btn"
                    className="bg-primary hover:bg-blue-600 text-white gap-2">
                    {isDone(active.id) ? <><CheckCircle size={18} weight="fill" /> Completada</> : "Marcar como leída"}
                  </Button>
                ) : (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    {isDone(active.id) ? <><CheckCircle size={18} weight="fill" className="text-primary" /> Vista</> : "Mira el video para avanzar"}
                  </span>
                )}
              </div>
              {active.resources?.length > 0 && (
                <div className="mt-4 bg-white border border-border rounded-lg p-4" data-testid="lesson-resources">
                  <p className="text-xs uppercase tracking-overline text-muted-foreground font-mono mb-2 flex items-center gap-1"><Paperclip size={14} /> Material descargable</p>
                  <ul className="space-y-1">
                    {active.resources.map((r, i) => (
                      <li key={i}>
                        <a href={r.url} target="_blank" rel="noreferrer" data-testid={`resource-${i}`}
                          className="text-sm text-primary hover:underline flex items-center gap-2"><DownloadSimple size={15} /> {r.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {mode === "quiz" && (
            <div className="bg-white border border-border rounded-lg p-8 animate-fade-up">
              <h2 className="font-display text-2xl font-800 tracking-tight mb-1">Evaluación final</h2>
              <p className="text-muted-foreground text-sm mb-6">Responde todas las preguntas. Nota mínima {course.passing_grade} y asistencia {course.min_attendance}%.</p>
              <div className="space-y-6">
                {course.quiz.map((q, qi) => (
                  <div key={q.id} data-testid={`quiz-q-${qi}`}>
                    <p className="font-medium mb-3">{qi + 1}. {q.text}</p>
                    <RadioGroup value={answers[q.id]?.toString()} onValueChange={(v) => setAnswers({ ...answers, [q.id]: parseInt(v) })}>
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2 bg-secondary/50 rounded-md px-3 py-2 hover:bg-secondary">
                          <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} data-testid={`quiz-${qi}-opt-${oi}`} />
                          <Label htmlFor={`${q.id}-${oi}`} className="cursor-pointer flex-1">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </div>
              <Button onClick={submitQuiz} disabled={submitting || Object.keys(answers).length < course.quiz.length}
                data-testid="quiz-submit-btn" className="mt-6 bg-primary hover:bg-blue-600 text-white gap-2">
                {submitting ? <CircleNotch className="animate-spin" /> : "Enviar evaluación"}
              </Button>
            </div>
          )}

          {mode === "result" && result && (
            <div className="bg-white border border-border rounded-lg p-10 text-center animate-fade-up" data-testid="quiz-result">
              {result.passed ? <Trophy size={64} weight="fill" className="text-accent mx-auto mb-4" />
                : <Circle size={64} className="text-muted-foreground mx-auto mb-4" />}
              <h2 className="font-display text-3xl font-800 tracking-tight">
                {result.passed ? "¡Aprobaste! 🎉" : "No alcanzaste la nota"}
              </h2>
              <p className="text-muted-foreground mt-2">
                Nota: <b className="text-foreground">{result.grade}</b> · {result.correct}/{result.total} correctas · Asistencia {result.attendance_pct}%
              </p>
              {result.passed ? (
                <>
                  <p className="text-sm text-primary mt-4 flex items-center justify-center gap-1">
                    <Certificate size={18} /> Certificado emitido: {result.certificate_folio}
                  </p>
                  <div className="flex gap-3 justify-center mt-6">
                    <Button onClick={() => navigate("/dashboard/certificados")} className="bg-primary text-white hover:bg-blue-600 gap-2">
                      <Certificate size={18} /> Ver certificado
                    </Button>
                  </div>
                </>
              ) : (
                <Button onClick={() => { setMode("quiz"); setResult(null); setAnswers({}); }} className="mt-6 bg-primary text-white hover:bg-blue-600">
                  Reintentar evaluación
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Sidebar lesson list */}
        <aside className="lg:col-span-1">
          <div className="bg-white border border-border rounded-lg p-4 sticky top-6">
            <p className="text-xs uppercase tracking-overline text-muted-foreground font-mono mb-3 px-1">Contenido</p>
            <div className="space-y-4">
              {course.modules.map((m) => (
                <div key={m.id}>
                  <p className="text-sm font-semibold mb-1.5 px-1">{m.title}</p>
                  <ul className="space-y-0.5">
                    {m.lessons.map((l) => (
                      <li key={l.id}>
                        <button onClick={() => { setActive({ ...l, moduleTitle: m.title }); setMode("lesson"); lastSent.current = 0; }}
                          data-testid={`lesson-item-${l.id}`}
                          className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm transition-transform hover:-translate-y-[1px] ${
                            active?.id === l.id ? "bg-primary/10 text-primary" : "hover:bg-secondary"}`}>
                          {isDone(l.id) ? <CheckCircle size={16} weight="fill" className="text-primary shrink-0" />
                            : l.type === "video" ? <PlayCircle size={16} className="shrink-0" /> : <FileText size={16} className="shrink-0" />}
                          <span className="line-clamp-1 flex-1">{l.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <Button onClick={() => setMode("quiz")} disabled={!allComplete}
                data-testid="start-quiz-btn"
                className="w-full bg-accent hover:bg-amber-500 text-white gap-2 disabled:opacity-50">
                {allComplete ? <ListChecks size={18} /> : <Lock size={16} />} Evaluación final
              </Button>
              {!allComplete && <p className="text-xs text-muted-foreground text-center mt-2">Completa todas las lecciones para habilitarla.</p>}
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={surveyOpen} onOpenChange={setSurveyOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-display font-800 flex items-center gap-2"><Star size={22} className="text-accent" weight="fill" /> Encuesta de satisfacción</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-5" data-testid="survey-questions">
            {surveyCfg.questions.map((q) => (
              <div key={q.id}>
                <p className="text-sm font-medium mb-2">{q.text}</p>
                {q.type === "rating" && (
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setSa({ ...sa, [q.id]: n })} data-testid={`survey-star-${n}`}>
                        <Star size={30} weight={n <= (sa[q.id] || 0) ? "fill" : "regular"} className={n <= (sa[q.id] || 0) ? "text-accent" : "text-muted-foreground"} />
                      </button>
                    ))}
                  </div>
                )}
                {q.type === "text" && (
                  <Textarea value={sa[q.id] || ""} onChange={(e) => setSa({ ...sa, [q.id]: e.target.value })}
                    placeholder="Tu respuesta (opcional)" data-testid="survey-comment" />
                )}
                {q.type === "choice" && (
                  <RadioGroup value={sa[q.id]} onValueChange={(v) => setSa({ ...sa, [q.id]: v })}>
                    {(q.options || []).map((o, i) => (
                      <div key={i} className="flex items-center gap-2 bg-secondary/50 rounded-md px-3 py-2">
                        <RadioGroupItem value={o} id={`${q.id}-${i}`} data-testid={`survey-choice-${q.id}-${i}`} />
                        <Label htmlFor={`${q.id}-${i}`} className="cursor-pointer flex-1">{o}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={submitSurvey} className="bg-primary text-white hover:bg-blue-600" data-testid="survey-submit">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
